import { Box, Input, InputGroup, InputLeftElement, InputRightElement, Kbd } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useSub } from "../../common/subscription/useSub";
import { urls } from "../../urls";
import { useHomeLabel } from "./useHomeLabel";

/**
 * The way into search from the home page: type a question, press enter, and it is asked of the
 * contract search — which answers in natural language rather than handing back a filtered table.
 *
 * The placeholder names contracts and nothing else, because contracts are all that is indexed.
 * Inviting a search for suppliers, procurements or needs would be a promise the search cannot keep,
 * and the reader would only find that out after typing.
 *
 * The question travels in the URL rather than being retyped on arrival. `/` focuses the field from
 * anywhere on the page, the shortcut people already expect from search bars.
 */
export const HomeSearchBar = () => {
  const navigate = useNavigate();
  const homeLabel = useHomeLabel();
  const { canUseContractSearch } = useSub();
  const inputRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState("");

  useEffect(() => {
    const focusOnSlash = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      // Not while the reader is typing somewhere else — "/" is a character before it is a shortcut.
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", focusOnSlash);
    return () => window.removeEventListener("keydown", focusOnSlash);
  }, []);

  // The search itself is the contract search; without both the subscription and a contract view
  // role there is nowhere for the question to go.
  if (!canUseContractSearch) return null;

  const ask = () => {
    const question = term.trim();
    if (question) navigate(`${urls.app.contracts.search}?q=${encodeURIComponent(question)}`);
  };

  return (
    <Box mb="4">
      <InputGroup size="lg">
        <InputLeftElement pointerEvents="none" h="full">
          <FiSearch color="var(--chakra-colors-smMuted)" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={term}
          bg="smBackground"
          borderColor="smBorder"
          rounded="xl"
          fontSize="md"
          placeholder={homeLabel("Ask about your contracts…")}
          _placeholder={{ color: "smMuted" }}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") ask();
          }}
        />
        <InputRightElement h="full" pointerEvents="none">
          <Kbd color="smMuted" borderColor="smBorder">
            /
          </Kbd>
        </InputRightElement>
      </InputGroup>
    </Box>
  );
};
